import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, compressImage, rupee, fmtDay, LANGS, waUrl } from "../lib/api";
import { LEGAL } from "../lib/legal";
import Voice from "../components/Voice";
import Review from "../components/Review";
import { AiChip, AiLoader, Chips, ErrorCard, Header, MoneyNote, Sheet } from "../components/bits";
import { Md } from "./Legal";
import { useT } from "../lib/i18n";

const KINDS = [["auto", "Auto-detect"], ["milk", "Milk card"], ["dhobi", "Dhobi slip"], ["grocery", "Grocery list"], ["kirana", "Kirana udhaari"], ["school", "School note"], ["whatsapp", "WhatsApp screenshot"], ["other", "Other"]];

function AiIntro({ mode }) {
  const [open, setOpen] = useState(mode !== "type" && !localStorage.getItem("ul_ai_seen"));
  const close = () => { localStorage.setItem("ul_ai_seen", "1"); setOpen(false); };
  return (
    <Sheet open={open} onClose={close} testid="ai-intro-sheet">
      <h2 className="font-display text-2xl font-extrabold mb-3">{LEGAL.ai.title}</h2>
      <Md text={LEGAL.ai.body} />
      <button data-testid="ai-intro-gotit" className="ul-btn ul-primary w-full mt-4" onClick={close}>Got it</button>
    </Sheet>
  );
}

const Row = ({ l, r, bold }) => {
  const t = useT();
  return (
  <div className="flex justify-between py-1.5"><span className="text-[#6B6680]">{t(l)}</span><span className={bold ? "font-display text-xl font-extrabold text-[#6A5AE0]" : "font-bold"}>{r}</span></div>
  );
};

function Receipt({ kind, r }) {
  if (!r || kind === "grocery") return null;
  return (
    <div className="ul-card p-5 font-mono-ish" data-testid="scan-receipt" style={{ backgroundImage: "repeating-linear-gradient(0deg,#fff,#fff 27px,#F7F5FE 28px)" }}>
      <div className="flex justify-between items-center mb-2"><AiChip text={`AI read this ${KINDS.find((k) => k[0] === kind)?.[1].toLowerCase() || "paper"}`} /></div>
      {kind === "milk" && (<>
        <Row l="Month" r={`${r.month}${r.name ? ` · ${r.name}` : ""}`} />
        <Row l="Days delivered" r={<span data-testid="milk-delivered">{r.delivered} of {r.days_in_month}</span>} />
        <Row l="Missed" r={r.missed.length ? r.missed.join(", ") : "none"} />
        <div className="receipt-line my-2" />
        <Row l={`${r.delivered} days × ${r.litres} L × ${rupee(r.rate)}`} r={<span data-testid="milk-expected">{rupee(r.expected)}</span>} bold />
        {r.app_log?.length > 0 && <p className="text-sm mt-2" style={{ color: r.log_matches ? "#0F8C80" : "#B45309" }}>{r.log_matches ? "✓ Matches your 'No milk today' log" : `Your app log says no milk on ${r.app_log.join(", ")}. Please check.`}</p>}
      </>)}
      {kind === "dhobi" && (<>
        {r.slip_no && <Row l="Slip" r={`#${r.slip_no}`} />}
        {r.items.map((i, k) => <Row key={k} l={`${i.name} × ${i.pieces}`} r={rupee(i.pieces * i.rate)} />)}
        <div className="receipt-line my-2" />
        <Row l="Total pieces" r={r.pieces} />
        <Row l={`Ready on ${fmtDay(r.delivery)}`} r={rupee(r.total)} bold />
      </>)}
      {kind === "kirana" && (<>
        {r.shop && <Row l="Shop" r={r.shop} />}
        {r.entries.map((e, k) => <Row key={k} l={e.date} r={rupee(e.amount)} />)}
        <div className="receipt-line my-2" />
        <Row l="Total udhaari" r={<span data-testid="kirana-total">{rupee(r.total)}</span>} bold />
        <p className="text-sm text-[#6B6680] mt-1">You decide when and how to pay.</p>
      </>)}
      {kind === "school" && (<>
        <Row l="Project" r={`${r.child || ""} ${r.project || ""}`} />
        <Row l="Deadline" r={fmtDay(r.deadline)} />
        <div className="receipt-line my-2" />
        {r.items.map((i, k) => <p key={k}>• {i}</p>)}
      </>)}
      {r.text && <p className="text-sm whitespace-pre-wrap text-[#6B6680]">{r.text.slice(0, 500)}</p>}
      {kind !== "school" && !r.text && <MoneyNote />}
    </div>
  );
}

function BillCheck({ r }) {
  const [asked, setAsked] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lang, setLang] = useState("मराठी");
  const [msg, setMsg] = useState("");
  const file = useRef();
  const t = useT();
  const check = async (image) => {
    setBusy(true);
    try { setRes((await api.post("/ai/bill-check", { asked: image ? null : asked, image, expected: r.expected, litres: r.litres, rate: r.rate })).data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const draft = async () => {
    try {
      const { data } = await api.post("/ai/draft", { language: lang, purpose: "Politely ask the dudhwala to check the milk bill before payment",
        facts: `asked ₹${res.asked}, expected ₹${res.expected} for ${r.delivered} days delivered, missed days ${r.missed.join(", ")}, rate ₹${r.rate}/L` });
      setMsg(data.text);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="ul-card p-4 space-y-3" data-testid="bill-check">
      <p className="font-bold">{t("What amount did the dudhwala ask for?")}</p>
      <div className="flex gap-2">
        <input data-testid="bill-asked-input" type="number" inputMode="numeric" className="ul-input" placeholder="₹ amount" value={asked} onChange={(e) => setAsked(e.target.value)} />
        <button data-testid="bill-check-btn" disabled={!asked || busy} onClick={() => check()} className="ul-btn ul-primary">{t("Check")}</button>
      </div>
      <button data-testid="bill-scan-btn" className="text-sm text-[#6A5AE0] font-bold underline" onClick={() => file.current.click()}>{t("or scan their WhatsApp message")}</button>
      <input ref={file} type="file" accept="image/*" hidden onChange={async (e) => e.target.files[0] && check(await compressImage(e.target.files[0]))} />
      {busy && <p className="ul-shimmer font-bold">Checking the numbers…</p>}
      {res && (res.diff === 0 ? (
        <div className="rounded-xl p-3" style={{ background: "#DDF7F1", color: "#0F8C80" }} data-testid="bill-match">✓ Asked {rupee(res.asked)} matches the expected amount.</div>
      ) : (
        <div className="rounded-xl p-4" style={{ background: "#FEF3E2", color: "#92400E" }} data-testid="bill-mismatch">
          <p className="font-bold">Asked {rupee(res.asked)} · Expected {rupee(res.expected)}</p>
          <p data-testid="bill-diff">{rupee(Math.abs(res.diff))} {res.diff > 0 ? "more" : "less"}{res.days && Number.isInteger(res.days) ? ` (${res.days} day${res.days > 1 ? "s" : ""} × ${rupee(res.per_day)}${res.diff > 0 ? " · maybe the missed days were charged" : ""})` : ""}. {t("Please check before paying.")}</p>
        </div>
      ))}
      {res && res.diff !== 0 && (
        <div className="space-y-2">
          <p className="text-sm font-bold">{t("✍️ Draft a polite message to the dudhwala")}</p>
          <Chips multi={false} options={["मराठी", "हिंदी", "English"]} value={lang} onChange={(v) => setLang(v || "मराठी")} testid="draft-lang" />
          <button data-testid="draft-msg-btn" className="ul-btn ul-ghost w-full" onClick={draft}>{t("Write the message")}</button>
          {msg && <><p className="text-sm whitespace-pre-wrap bg-[#F7F5FE] rounded-xl p-3" data-testid="draft-msg-text">{msg}</p>
            <a href={waUrl(msg)} target="_blank" rel="noreferrer" className="ul-btn ul-wa w-full" data-testid="draft-wa-btn">{t("Send on WhatsApp")}</a></>}
        </div>
      )}
      <MoneyNote />
    </div>
  );
}

function Scan({ onResult }) {
  const [kind, setKind] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [res, setRes] = useState(null);
  const file = useRef();
  const t = useT();
  const pick = async (f) => {
    if (!f) return;
    if (f.type === "application/pdf") return toast("Got a PDF? Take a screenshot and upload it.");
    setBusy(true); setErr(null);
    try {
      const image = await compressImage(f);
      const { data } = await api.post("/ai/scan", { image, kind });
      if (data.unclear) setErr("Photo not clear. Please try again in good light.");
      else setRes(data);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); if (file.current) file.current.value = ""; }
  };
  if (busy) return <AiLoader kind="scan" />;
  if (res) return (
    <div className="space-y-4">
      <Receipt kind={res.kind} r={res.receipt} />
      {res.kind === "milk" && <BillCheck r={res.receipt} />}
      <Review result={res} onSaved={onResult} onEdit={() => setRes(null)} />
    </div>
  );
  return (
    <div className="space-y-4">
      <p className="ul-label">{t("What is it?")}</p>
      <Chips multi={false} options={KINDS} value={kind} onChange={(v) => setKind(v || "auto")} testid="scan-kind" />
      {err && <ErrorCard text={err} onRetry={() => file.current.click()} />}
      <button data-testid="scan-photo-btn" onClick={() => file.current.click()} className="ul-card w-full py-10 flex flex-col items-center gap-2 border-2 border-dashed !border-[#D9D2FF]">
        <span className="text-5xl">📷</span><b className="font-display text-xl">{t("Take a photo or upload")}</b>
        <span className="text-sm text-[#6B6680]">{t("Milk card · dhobi slip · kirana note · school note · screenshot")}</span>
      </button>
      <input ref={file} data-testid="scan-file-input" type="file" accept="image/*" capture="environment" hidden onChange={(e) => pick(e.target.files[0])} />
      <p className="text-xs text-center text-[#6B6680]">{t("Got a PDF? Take a screenshot and upload it. Photos are not kept after reading.")}</p>
    </div>
  );
}

export default function Unload() {
  const { mode } = useParams();
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [heard, setHeard] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [res, setRes] = useState(null);
  const t = useT();
  const done = () => nav("/");
  const extract = async () => {
    setBusy(true); setErr(null);
    try { setRes((await api.post("/ai/extract", { text, source: mode === "speak" ? "voice" : "type" })).data); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const title = { speak: "Speak your mind", scan: "Scan a paper", type: "Type or paste" }[mode] || "Unload";
  return (
    <div>
      <AiIntro mode={mode} />
      <Header title={title} kicker="AI suggests · You decide" />
      {mode === "scan" ? <Scan onResult={done} /> : busy ? <AiLoader kind={mode === "speak" ? "voice" : "type"} /> : res ? (
        <Review result={res} onSaved={done} onEdit={() => setRes(null)} />
      ) : (
        <div className="space-y-4">
          {mode === "speak" && !heard && (
            <Voice onText={(x) => { setText(x); setHeard(true); }} onError={(m) => toast(m)} label={t("Tap and speak, in any language")} />
          )}
          {mode === "speak" && !heard && <p className="text-sm text-center text-[#6B6680]">e.g. "Bai 3 din nahi aayegi, Aaji chi goli sampat aali aahe, gas booking pending hai aur Kabir cha project Monday la aahe"</p>}
          {(mode === "type" || heard) && (
            <div className="ul-card p-4">
              {heard && <p className="mb-2"><AiChip text="AI heard" /></p>}
              <textarea data-testid="unload-textarea" rows={7} className="w-full outline-none resize-none text-[16px]" value={text} onChange={(e) => setText(e.target.value)}
                placeholder={t("Type or paste anything: a WhatsApp message, a list, your thoughts…")} />
            </div>
          )}
          {err && <ErrorCard text={err} onRetry={extract} />}
          {(mode === "type" || heard) && (
            <div className="flex gap-2">
              <button data-testid="unload-find-tasks-btn" disabled={!text.trim()} onClick={extract} className="ul-btn ul-primary flex-1">{t("✨ Find tasks")}</button>
              {heard && <button data-testid="unload-rerecord-btn" onClick={() => { setHeard(false); setText(""); }} className="ul-btn ul-ghost">{t("Re-record")}</button>}
            </div>
          )}
          {mode === "speak" && !heard && <button data-testid="unload-type-instead" className="w-full text-center text-[#6A5AE0] font-bold underline" onClick={() => setHeard(true)}>{t("Type instead")}</button>}
          <p className="text-xs text-center text-[#6B6680]">{t("Recordings are not kept after reading. Nothing is saved until you confirm.")}</p>
        </div>
      )}
    </div>
  );
}

export { LANGS };
